import { useEffect, useRef, useState } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';

import type { CommentType } from '@confluence/comments-data-manager/entry-points/commentDataManagerUtils.ts';

import type { InlineCommentsMode } from '../inlineCommentsTypes.ts';
import {
	getCommentContentStats,
	measureCommentContent,
	type AdfNode,
	type CommentRenderLocation,
} from '../commentHeightAnalyticsUtils.ts';

/**
 * How long to wait after a comment first becomes visible before measuring it, so late layout
 * (images, smart cards, macros) has a chance to settle.
 */
export const COMMENT_HEIGHT_SETTLE_DELAY_MS = 1000;

// One shared observer for every comment on the page, rather than one per comment. A zero threshold
// means any visible pixel counts as seen, so comments taller than the viewport still count.
let sharedObserver: IntersectionObserver | undefined;
const onSeenCallbacks = new Map<Element, () => void>();

const getSharedObserver = (): IntersectionObserver | undefined => {
	if (sharedObserver || typeof IntersectionObserver === 'undefined') {
		return sharedObserver;
	}
	sharedObserver = new IntersectionObserver(
		(entries) => {
			entries.forEach((entry) => {
				if (!entry.isIntersecting) return;
				const onSeen = onSeenCallbacks.get(entry.target);
				if (!onSeen) return;
				onSeenCallbacks.delete(entry.target);
				sharedObserver?.unobserve(entry.target);
				onSeen();
			});
		},
		{ threshold: 0 },
	);
	return sharedObserver;
};

const observeUntilSeen = (element: Element, onSeen: () => void): (() => void) => {
	const observer = getSharedObserver();
	if (!observer) return () => {};

	onSeenCallbacks.set(element, onSeen);
	observer.observe(element);
	return () => {
		onSeenCallbacks.delete(element);
		observer.unobserve(element);
	};
};

// Comments already reported in this browser session, so each comment is sent at most once per
// page and surface even when it re-mounts (thread re-opened, panel toggled, SPA navigation).
const reportedComments = new Set<string>();

/** Test-only: clears module state shared between hook instances. */
export const resetCommentHeightAnalyticsForTesting = (): void => {
	sharedObserver?.disconnect();
	sharedObserver = undefined;
	onSeenCallbacks.clear();
	reportedComments.clear();
};

type UseCommentHeightAnalyticsParams = {
	/** Evaluate the experiment at the callsite and pass the result here. */
	isEnabled: boolean;
	pageId: string;
	contentType: string;
	commentId: string;
	commentType: CommentType;
	renderLocation: CommentRenderLocation;
	isReply: boolean;
	mode: InlineCommentsMode;
	adf: AdfNode | null | undefined;
};

/**
 * Sends one `commentContent measured` operational event per comment the first time it is seen,
 * recording its full rendered height so the "Show more" cutoff can be set from real data.
 *
 * Returns a ref callback to attach to the element that wraps the rendered comment, or undefined
 * when disabled so nothing is stored and no extra render happens.
 */
export const useCommentHeightAnalytics = ({
	isEnabled,
	pageId,
	contentType,
	commentId,
	commentType,
	renderLocation,
	isReply,
	mode,
	adf,
}: UseCommentHeightAnalyticsParams): ((element: HTMLElement | null) => void) | undefined => {
	const [element, setElement] = useState<HTMLElement | null>(null);
	const { createAnalyticsEvent } = useAnalyticsEvents();

	// Values that only describe the event are read when it is sent, not used as effect dependencies.
	// Their references can change on renders where nothing meaningful changed (CommentBody's
	// `parseContent` cache is shared by every comment, so `adf` is often a fresh object), and
	// re-running the effect would cancel the settle timer and could stop the event ever being sent.
	const latestRef = useRef({ contentType, commentType, isReply, mode, adf, createAnalyticsEvent });
	useEffect(() => {
		latestRef.current = { contentType, commentType, isReply, mode, adf, createAnalyticsEvent };
	});

	useEffect(() => {
		if (!isEnabled || !element || !commentId) return;

		const reportKey = `${pageId}:${commentId}:${renderLocation}`;
		if (reportedComments.has(reportKey)) return;

		let settleTimeout: ReturnType<typeof setTimeout> | undefined;

		const stopObserving = observeUntilSeen(element, () => {
			settleTimeout = setTimeout(() => {
				settleTimeout = undefined;
				if (!element.isConnected || reportedComments.has(reportKey)) return;

				const measurement = measureCommentContent(element);
				if (!measurement) return;

				const latest = latestRef.current;
				reportedComments.add(reportKey);
				latest
					.createAnalyticsEvent({
						type: 'sendOperationalEvent',
						data: {
							action: 'measured',
							actionSubject: 'commentContent',
							source: latest.mode === 'edit' ? 'editPageScreen' : 'viewPageScreen',
							objectType: latest.contentType || 'page',
							objectId: pageId,
							attributes: {
								commentId,
								commentType: latest.commentType,
								location: renderLocation,
								isReply: latest.isReply,
								mode: latest.mode,
								contentType: latest.contentType,
								...measurement,
								viewportHeight: window.innerHeight,
								viewportWidth: window.innerWidth,
								...getCommentContentStats(latest.adf),
							},
						},
					})
					.fire();
			}, COMMENT_HEIGHT_SETTLE_DELAY_MS);
		});

		return () => {
			stopObserving();
			if (settleTimeout !== undefined) clearTimeout(settleTimeout);
		};
		// Only values that identify which comment is measured belong here; see `latestRef` above.
	}, [isEnabled, element, pageId, commentId, renderLocation]);

	// No ref when disabled: storing the element would re-render every comment on mount for nothing.
	// React calls the new ref with the element if the experiment turns on later.
	return isEnabled ? setElement : undefined;
};
